import { useCookies } from '@/composables/cookies'
import {
  CJ_AID_COOKIE_NAME,
  CJ_AID_FALLBACK_QUERY_PARAM,
  CJ_AID_QUERY_PARAM,
  CJ_APPSFLYER_ID_COOKIE_NAME,
  CJ_COOKIE_NAME,
  CJ_EVENT_MAX_LENGTH,
  CJ_EVENT_QUERY_PARAM,
  CJ_FIELD_VALUE_PATTERN,
  CJ_PID_COOKIE_NAME,
  CJ_PID_FALLBACK_QUERY_PARAM,
  CJ_PID_QUERY_PARAM,
} from './consts'

type GetCjEventIdOptions = {
  search?: string
}

export type CjAttribution = {
  cj_event_id?: string
  aid?: string
  pid?: string
  appsflyer_id?: string
}

type GetCjAttributionOptions = GetCjEventIdOptions & {
  cjEventId?: string | null
}

export { initCjAffiliateAttribution, persistCjEvent, tryPersistCjEvent } from './persist'

type UseCjAffiliateResult = {
  getCjAttribution: (options?: GetCjAttributionOptions) => CjAttribution | null
  getCjEventId: (options?: GetCjEventIdOptions) => string | null
  getCjEventIdFromSearch: (search: string) => string | null
}

export function normalizeCjEventId (value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmedValue = value.trim()

  if (!trimmedValue || trimmedValue.length > CJ_EVENT_MAX_LENGTH) {
    return null
  }

  return trimmedValue
}

export function normalizeCjFieldValue (value: unknown): string | null {
  const normalizedValue = normalizeCjEventId(value)

  if (!normalizedValue || !CJ_FIELD_VALUE_PATTERN.test(normalizedValue)) {
    return null
  }

  return normalizedValue
}

export function getQueryParamFromSearch (paramName: string, search: string): string | null {
  const params = new URLSearchParams(search)

  for (const [key, value] of params.entries()) {
    if (key.toLowerCase() === paramName.toLowerCase()) {
      return normalizeCjEventId(value)
    }
  }

  return null
}

export function getCjEventIdFromSearch (search: string): string | null {
  return getQueryParamFromSearch(CJ_EVENT_QUERY_PARAM, search)
}

function getCjFieldFromCookieOrSearchParams (
  cookieName: string,
  queryParams: string[],
  search: string,
  getCookie: (name: string) => unknown
): string | null {
  const cookieValue = normalizeCjEventId(getCookie(cookieName))

  if (cookieValue) {
    return cookieValue
  }

  for (const queryParam of queryParams) {
    const queryValue = getQueryParamFromSearch(queryParam, search)

    if (queryValue) {
      return queryValue
    }
  }

  return null
}

function buildCjAttribution (fields: CjAttribution): CjAttribution | null {
  const cj = Object.entries(fields).reduce<CjAttribution>((acc, [key, value]) => {
    if (value) {
      acc[key as keyof CjAttribution] = value
    }

    return acc
  }, {})

  return Object.keys(cj).length > 0 ? cj : null
}

export function getCjAttribution (options?: GetCjAttributionOptions): CjAttribution | null {
  const { getCookie } = useCookies()
  const search = options?.search ?? (typeof window !== 'undefined' ? window.location.search : '')
  const cjEventId = normalizeCjEventId(options?.cjEventId) ?? getCjEventId({ search })

  return buildCjAttribution({
    cj_event_id: cjEventId,
    aid: getCjFieldFromCookieOrSearchParams(
      CJ_AID_COOKIE_NAME,
      [CJ_AID_QUERY_PARAM, CJ_AID_FALLBACK_QUERY_PARAM],
      search,
      getCookie
    ),
    pid: getCjFieldFromCookieOrSearchParams(
      CJ_PID_COOKIE_NAME,
      [CJ_PID_QUERY_PARAM, CJ_PID_FALLBACK_QUERY_PARAM],
      search,
      getCookie
    ),
    appsflyer_id: normalizeCjEventId(getCookie(CJ_APPSFLYER_ID_COOKIE_NAME)),
  })
}

export function getCjEventId (options?: GetCjEventIdOptions): string | null {
  const { getCookie } = useCookies()
  const cookieValue = normalizeCjEventId(getCookie(CJ_COOKIE_NAME))

  if (cookieValue) {
    return cookieValue
  }

  const search = options?.search ?? (typeof window !== 'undefined' ? window.location.search : '')

  return getCjEventIdFromSearch(search)
}

export function useCjAffiliate (): UseCjAffiliateResult {
  return {
    getCjAttribution,
    getCjEventId,
    getCjEventIdFromSearch,
  }
}
